import TadaIcon from '@apps-common/ui-icons/TaskingDashboard.jsx';
import CloseIcon from '@mui/icons-material/Close';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  Toolbar,
  Tooltip,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {forwardRef, useCallback} from 'react';
import {monospace} from '../theme.js';
import {formatCoords} from '../util/map.js';
import CopyMenuButton from './CopyMenuButton.jsx';

const StyledBottomToolbar = styled(Toolbar)({
  paddingLeft: 0,
  paddingRight: 0,
});

const StyledListItem = styled(ListItem)({
  paddingTop: 0,
  paddingBottom: 0,
});

const StyledListSubheader = styled(ListSubheader)(({theme}) => ({
  color: theme.palette.primary.contrastText,
  flex: 1,
  fontWeight: 600,
}));

const primaryProps = {
  variant: 'caption',
  color: 'inherit',
  noWrap: true,
};
const secondaryProps = {
  variant: 'subtitle2',
  color: 'inherit',
  noWrap: true,
  ...monospace,
};

function defined(value) {
  return value !== undefined;
}

/**
 * The popup shows information about the clicked feature in the map.
 * It can be shown expanded or collapsed.
 */
const Popup = forwardRef(
  (
    {
      center,
      deeplink = false,
      expanded,
      feature,
      resultInfo,
      toggleExpanded,
      onClose,
    },
    ref,
  ) => {
    const coords = formatCoords(center);
    const score = feature?.get('score');

    const handleClick = useCallback(() => {
      toggleExpanded();
    }, [toggleExpanded]);

    const {
      object_diagonal_m,
      object_area_m2,
      object_class_label,
      observed,
      source_cloud_cover,
      source_item_id,
      source_item_type,
      model_id,
      caption,
    } = resultInfo?.properties ?? {};

    return (
      <Paper
        ref={ref}
        elevation={0}
        sx={{
          backgroundColor: '#222924',
          padding: expanded ? 2 : 1,
          paddingTop: 1,
          width: resultInfo ? 325 : 275,
        }}
      >
        <Toolbar
          variant="dense"
          disableGutters
          sx={{justifyContent: 'flex-end'}}
        >
          {!expanded && (
            <>
              {deeplink && (
                <IconButton
                  href={`https://www.planet.com/tasking/orders/new/?geometry=POINT(${center[0]}%20${center[1]})`}
                  target="_blank"
                  rel="noopener"
                  size="small"
                  color="inherit"
                >
                  <TadaIcon fontSize="small" />
                </IconButton>
              )}
              {feature && (
                <CopyMenuButton
                  fid={feature.get('id')}
                  modelId={model_id}
                  sourceItemId={source_item_id}
                  size="small"
                />
              )}

              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  marginX: 1,
                }}
              />
            </>
          )}

          {toggleExpanded && (
            <IconButton size="small" onClick={handleClick} color="inherit">
              {expanded ? (
                <UnfoldLessIcon
                  fontSize="small"
                  sx={{transform: 'rotate(45deg)'}}
                />
              ) : (
                <UnfoldMoreIcon
                  fontSize="small"
                  sx={{transform: 'rotate(45deg)'}}
                />
              )}
            </IconButton>
          )}
          <IconButton size="small" onClick={onClose} color="inherit">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Toolbar>
        {!expanded && (
          <>
            <Box display="flex" flexDirection="column">
              {defined(object_class_label) && (
                <ListItemText
                  primary="Class"
                  primaryTypographyProps={primaryProps}
                  secondary={object_class_label}
                  secondaryTypographyProps={secondaryProps}
                />
              )}
              {defined(score) && (
                <ListItemText
                  primary="Confidence score"
                  primaryTypographyProps={primaryProps}
                  secondary={Number(score).toFixed(3)}
                  secondaryTypographyProps={secondaryProps}
                />
              )}
              {!defined(score) && defined(object_area_m2) && (
                <ListItemText
                  primary="Change area m²"
                  primaryTypographyProps={primaryProps}
                  secondary={Number(object_area_m2).toFixed(1)}
                  secondaryTypographyProps={secondaryProps}
                />
              )}
              <ListItemText
                primary="Location"
                primaryTypographyProps={primaryProps}
                secondary={coords}
                secondaryTypographyProps={secondaryProps}
              />
              {defined(caption) && (
                <Tooltip title={caption} arrow>
                  <ListItemText
                    primary="Caption"
                    primaryTypographyProps={primaryProps}
                    secondary={caption}
                    secondaryTypographyProps={secondaryProps}
                  />
                </Tooltip>
              )}
            </Box>
          </>
        )}
        {expanded && (
          <>
            <List
              dense
              disablePadding
              subheader={
                <Box display="flex" flexDirection="row" alignItems="center">
                  <StyledListSubheader component="div">
                    DETECTION PROPERTIES
                  </StyledListSubheader>
                  {feature && (
                    <CopyMenuButton
                      fid={feature.get('id')}
                      modelId={model_id}
                      sourceItemId={source_item_id}
                      size="small"
                    />
                  )}
                </Box>
              }
              sx={{
                overflowY: 'auto',
                marginTop: 1,
              }}
            >
              {defined(object_class_label) && (
                <ListItemText
                  primary="Class"
                  primaryTypographyProps={primaryProps}
                  secondary={object_class_label}
                  secondaryTypographyProps={secondaryProps}
                />
              )}
              {defined(score) && (
                <StyledListItem disableGutters>
                  <ListItemText
                    primary="Confidence score"
                    primaryTypographyProps={primaryProps}
                    secondary={Number(score).toFixed(3)}
                    secondaryTypographyProps={secondaryProps}
                  />
                </StyledListItem>
              )}
              {defined(object_diagonal_m) && (
                <StyledListItem disableGutters>
                  <ListItemText
                    primary="Diagonal length"
                    primaryTypographyProps={primaryProps}
                    secondary={`${object_diagonal_m.toFixed(1)} m`}
                    secondaryTypographyProps={secondaryProps}
                  />
                </StyledListItem>
              )}
              {defined(observed) && (
                <StyledListItem disableGutters>
                  <ListItemText
                    primary="Timestamp"
                    primaryTypographyProps={primaryProps}
                    secondary={`${observed.split('.')[0]} UTC`}
                    secondaryTypographyProps={secondaryProps}
                  />
                </StyledListItem>
              )}
              {defined(source_item_type) && (
                <StyledListItem disableGutters>
                  <ListItemText
                    primary="Image type"
                    primaryTypographyProps={primaryProps}
                    secondary={source_item_type}
                    secondaryTypographyProps={secondaryProps}
                  />
                </StyledListItem>
              )}
              {defined(source_cloud_cover) && (
                <StyledListItem disableGutters>
                  <ListItemText
                    primary="Cloud cover"
                    primaryTypographyProps={primaryProps}
                    secondary={`${(source_cloud_cover * 100).toFixed(1)} %`}
                    secondaryTypographyProps={secondaryProps}
                  />
                </StyledListItem>
              )}
              <StyledListItem disableGutters>
                <ListItemText
                  primary="Location"
                  primaryTypographyProps={primaryProps}
                  secondary={coords}
                  secondaryTypographyProps={secondaryProps}
                />
              </StyledListItem>
            </List>
            {deeplink && (
              <StyledBottomToolbar>
                <Button
                  color="primary"
                  fullWidth
                  variant="contained"
                  startIcon={<TadaIcon />}
                  href={`https://www.planet.com/tasking/orders/new/?geometry=POINT(${center[0]}%20${center[1]})`}
                  target="_blank"
                  rel="noopener"
                >
                  Task high-res image
                </Button>
              </StyledBottomToolbar>
            )}
          </>
        )}
      </Paper>
    );
  },
);

Popup.propTypes = {
  center: PropTypes.array,
  deeplink: PropTypes.bool,
  expanded: PropTypes.bool.isRequired,
  feature: PropTypes.object,
  resultInfo: PropTypes.object,
  toggleExpanded: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default Popup;
